import React, { useEffect, useRef, useState } from "react";
import { FiUploadCloud, FiX, FiLoader } from "react-icons/fi";
import { API_BASE } from "../../config";
import Loader from "./Loader";
import { compressImage } from "../../utils/compressImage";

function SiteSettingsManager({ token }) {
  const [counters, setCounters] = useState([]);
  const [heroImage, setHeroImage] = useState("");
  const [aboutImage, setAboutImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(null); // "hero" | "about" | null

  // new file picked but not yet uploaded (per slot)
  const [heroFile, setHeroFile] = useState(null);
  const [aboutFile, setAboutFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState("");
  const [aboutPreview, setAboutPreview] = useState("");
  const [heroIsVideo, setHeroIsVideo] = useState(false);
  const [aboutIsVideo, setAboutIsVideo] = useState(false);

  const heroBlobRef = useRef(null);
  const aboutBlobRef = useRef(null);
  const heroInputRef = useRef(null);
  const aboutInputRef = useRef(null);

  const authHeader = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const revoke = (ref) => {
    if (ref.current) {
      URL.revokeObjectURL(ref.current);
      ref.current = null;
    }
  };

  useEffect(
    () => () => {
      revoke(heroBlobRef);
      revoke(aboutBlobRef);
    },
    []
  );

  useEffect(() => {
    fetch(`${API_BASE}/settings`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load settings");
        return res.json();
      })
      .then((data) => {
        if (data.counters) setCounters(data.counters);
        const hero = data.heroImage || "";
        const about = data.aboutImage || "";
        setHeroImage(hero);
        setAboutImage(about);
        setHeroPreview(hero);
        setAboutPreview(about);
        // Initialise video flags so saved videos render as <video> on load
        setHeroIsVideo(/\.(mp4|webm|mov)(\?|$)/i.test(hero));
        setAboutIsVideo(/\.(mp4|webm|mov)(\?|$)/i.test(about));
      })
      .catch((err) => setMsg(err.message || "Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (index, field, value) => {
    const updated = [...counters];
    updated[index] = { ...updated[index], [field]: field === "value" ? parseInt(value) || 0 : value };
    setCounters(updated);
  };

  const addCounter = () => {
    setCounters([...counters, { label: "", value: 0, suffix: "" }]);
  };

  const removeCounter = (index) => {
    setCounters(counters.filter((_, i) => i !== index));
  };

  // Pick file → validate + compress images + instant local preview (upload deferred to Save)
  const handleFilePick = async (e, slot) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const maxMB = isVideo ? 50 : 10;

    if (!isImage && !isVideo) {
      setMsg("Only images (jpg, png, webp, gif) or videos (mp4, webm, mov) are allowed here");
      return;
    }
    if (file.size > maxMB * 1024 * 1024) {
      setMsg(`File too large — max ${maxMB}MB`);
      return;
    }

    setPreparing(slot);
    setMsg("");
    let store = file;
    if (isImage) {
      try {
        store = await compressImage(file);
      } catch {
        store = file;
      }
    }
    setPreparing(null);

    const blobUrl = URL.createObjectURL(store);
    if (slot === "hero") {
      revoke(heroBlobRef);
      heroBlobRef.current = blobUrl;
      setHeroFile(store);
      setHeroPreview(blobUrl);
      setHeroIsVideo(isVideo);
    } else {
      revoke(aboutBlobRef);
      aboutBlobRef.current = blobUrl;
      setAboutFile(store);
      setAboutPreview(blobUrl);
      setAboutIsVideo(isVideo);
    }
  };

  const clearImage = (slot) => {
    if (slot === "hero") {
      revoke(heroBlobRef);
      setHeroFile(null);
      setHeroPreview("");
      setHeroImage("");
      setHeroIsVideo(false);
      if (heroInputRef.current) heroInputRef.current.value = "";
    } else {
      revoke(aboutBlobRef);
      setAboutFile(null);
      setAboutPreview("");
      setAboutImage("");
      setAboutIsVideo(false);
      if (aboutInputRef.current) aboutInputRef.current.value = "";
    }
  };

  const uploadToS3 = async (file, folder) => {
    const presignRes = await fetch(`${API_BASE}/upload/presign`, {
      method: "POST",
      headers: authHeader,
      body: JSON.stringify({ fileName: file.name, fileType: file.type, folder }),
    });
    if (!presignRes.ok) {
      const data = await presignRes.json().catch(() => ({}));
      throw new Error(data.error || "Failed to get upload URL");
    }
    const { uploadUrl, publicUrl } = await presignRes.json();
    const uploadRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!uploadRes.ok) throw new Error("Failed to upload file to S3");
    return publicUrl;
  };

  const handleSave = async () => {
    if (saving || preparing) return;
    setMsg("");
    setSaving(true);
    try {
      let finalHero = heroImage;
      let finalAbout = aboutImage;

      if (heroFile) {
        setMsg("Uploading hero media…");
        finalHero = await uploadToS3(heroFile, "home");
      }
      if (aboutFile) {
        setMsg("Uploading about media…");
        finalAbout = await uploadToS3(aboutFile, "home");
      }

      const res = await fetch(`${API_BASE}/settings`, {
        method: "PUT",
        headers: authHeader,
        body: JSON.stringify({ counters, heroImage: finalHero, aboutImage: finalAbout }),
      });
      if (!res.ok) throw new Error("Failed to save settings");

      setHeroImage(finalHero);
      setAboutImage(finalAbout);
      setHeroFile(null);
      setAboutFile(null);
      revoke(heroBlobRef);
      revoke(aboutBlobRef);
      setHeroPreview(finalHero);
      setAboutPreview(finalAbout);
      // After save, re-evaluate isVideo from the saved URL (S3 URLs have extensions)
      setHeroIsVideo(isVideoUrl(finalHero));
      setAboutIsVideo(isVideoUrl(finalAbout));
      setMsg("Settings saved successfully");
    } catch (err) {
      setMsg(err.message || "Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) return <Loader />;

  const isVideoUrl = (url) => Boolean(url && /\.(mp4|webm|mov)(\?|$)/i.test(url));

  const renderMediaSlot = (slot, preview, slotIsVideo, inputRef, label, hint) => (
    <div className="settings-image-slot">
      <div className="settings-image-label">
        <strong>{label}</strong>
        <small>{hint}</small>
      </div>
      {preparing === slot ? (
        <div className="pm-upload-note">Optimizing image…</div>
      ) : preview ? (
        <div className="pm-upload-preview settings-image-preview">
          {slotIsVideo ? (
            <video src={preview} controls muted style={{ width: "100%", borderRadius: 8 }} />
          ) : (
            <img src={preview} alt={`${label} preview`} />
          )}
          <button type="button" className="pm-upload-remove" onClick={() => clearImage(slot)} title="Remove media">
            <FiX />
          </button>
          <label className="pm-upload-replace">
            <FiUploadCloud /> Replace
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              onChange={(e) => handleFilePick(e, slot)}
              hidden
            />
          </label>
        </div>
      ) : (
        <label className="pm-upload-dropzone" title={`Choose ${label.toLowerCase()}`}>
          <FiUploadCloud />
          <span>Click to upload {label.toLowerCase()}</span>
          <small>{hint}</small>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            onChange={(e) => handleFilePick(e, slot)}
            hidden
          />
        </label>
      )}
    </div>
  );

  return (
    <div className="settings-manager">
      <h2>Home Page Images &amp; Videos</h2>
      <p className="settings-desc">
        These images or videos appear on the website home page. Changes go live after saving.
      </p>
      <div className="settings-images">
        {renderMediaSlot(
          "hero",
          heroPreview,
          heroIsVideo,
          heroInputRef,
          "Hero Image / Video",
          'Top-right media next to "Global Trade, Simplified." — JPG, PNG, WEBP, GIF up to 10MB · MP4, WEBM up to 50MB'
        )}
        {renderMediaSlot(
          "about",
          aboutPreview,
          aboutIsVideo,
          aboutInputRef,
          "About Section Image / Video",
          'Media beside "Building Bridges Across Borders." — JPG, PNG, WEBP, GIF up to 10MB · MP4, WEBM up to 50MB'
        )}
      </div>

      <h2 style={{ marginTop: 32 }}>Homepage Counters</h2>
      <p className="settings-desc">
        These numbers appear on the home page counter section with animation.
      </p>

      {counters.map((c, i) => (
        <div key={i} className="settings-row">
          <input
            placeholder="Label (e.g. Years of Experience)"
            value={c.label}
            onChange={(e) => handleChange(i, "label", e.target.value)}
          />
          <input
            type="number"
            placeholder="Value"
            value={c.value}
            onChange={(e) => handleChange(i, "value", e.target.value)}
          />
          <input
            placeholder="Suffix"
            value={c.suffix}
            onChange={(e) => handleChange(i, "suffix", e.target.value)}
          />
          <button className="btn-danger" onClick={() => removeCounter(i)}>Remove</button>
        </div>
      ))}

      <div className="settings-actions">
        <button className="btn-secondary" onClick={addCounter} disabled={saving}>+ Add Counter</button>
        <button className="btn-primary" onClick={handleSave} disabled={saving || preparing}>
          {saving ? (
            <>
              <FiLoader className="btn-spin" /> Saving…
            </>
          ) : (
            "Save Settings"
          )}
        </button>
      </div>
      {msg && <p className="pm-msg">{msg}</p>}
    </div>
  );
}

export default SiteSettingsManager;
